import * as decoding from 'lib0/decoding';
import * as encoding from 'lib0/encoding';
import * as awarenessProtocol from 'y-protocols/awareness';

import { aPersonCanCollaborateWithOtherUsersOnAWhiteboard } from '@canvas/capability-summaries';
import { AwarenessChannel } from '@canvas/model/awareness';
import { AwarenessEncodingType } from '@canvas/protocol-types';
import {
  captureExceptionForCapability,
  CustomerImpact,
  EventClassification,
} from '@canvas/sentry-react';
import { type TransactionOrigin, YJS_DEFAULT, YjsOrigin } from '@canvas/transaction';

type AwarenessStateEncoderProtocol =
  | typeof awarenessProtocol.DefaultAwarenessStateEncoder
  | typeof awarenessProtocol.BinaryAwarenessStateEncoder;

/**
 * Handles the encoding, decoding, and format detection of awareness data.
 */
export class AwarenessDataHandler {
  /**
   * Encodes an awareness update for a given channel and client IDs.
   */
  public encodeUpdate(
    awareness: awarenessProtocol.Awareness,
    _channel: AwarenessChannel,
    clientIds: number[],
    encodingType: AwarenessEncodingType,
  ): Uint8Array {
    const stateEncoder =
      encodingType === AwarenessEncodingType.Binary
        ? awarenessProtocol.BinaryAwarenessStateEncoder
        : awarenessProtocol.DefaultAwarenessStateEncoder;

    const encoder = encoding.createEncoder();
    const data = awarenessProtocol.encodeAwarenessUpdate(
      awareness,
      clientIds,
      undefined,
      stateEncoder, // Specify encoder method to use
    );
    encoding.writeVarUint8Array(encoder, data);

    return encoding.toUint8Array(encoder);
  }

  /**
   * Safely decodes awareness data using explicit encoding type or fallback logic.
   */
  public decodeUpdate(
    awareness: awarenessProtocol.Awareness,
    channel: AwarenessChannel,
    data: Uint8Array,
    origin: TransactionOrigin,
    encodingType?: AwarenessEncodingType,
  ): void {
    const decoderMethod: AwarenessStateEncoderProtocol =
      encodingType === AwarenessEncodingType.Binary
        ? awarenessProtocol.BinaryAwarenessStateEncoder
        : awarenessProtocol.DefaultAwarenessStateEncoder;

    try {
      const decoder = decoding.createDecoder(data);
      awarenessProtocol.applyAwarenessUpdate(
        awareness,
        decoding.readVarUint8Array(decoder),
        new YjsOrigin(YJS_DEFAULT, origin as unknown as TransactionOrigin),
        decoderMethod,
      );
    } catch (error) {
      // Log decoding error for awareness data in Sentry
      captureExceptionForCapability(
        error,
        {
          tags: {
            captureLocation: 'awareness-data-handler.decodeUpdate',
            customerImpact: CustomerImpact.CUSTOMERS,
            eventClassification: EventClassification.BUG,
            channel: AwarenessChannel[channel],
            encodingType,
          },
        },
        aPersonCanCollaborateWithOtherUsersOnAWhiteboard,
      );

      // Do not re-throw. Essentially swallow awareness decoding errors
    }
  }
}
